/** apps-api serves component icons at exactly these sizes, so the source must be square and this big. */
export const REQUIRED_ICON_SIZE = 256;

export type IconRejection =
  | { reason: 'not-png' }
  | { reason: 'too-many' }
  | { reason: 'unreadable' }
  | { reason: 'wrong-size'; width: number; height: number };

export type IconCheck = { ok: true; previewUrl: string } | ({ ok: false } & IconRejection);

export const iconRejectionMessage = (rejection: IconRejection): string => {
  switch (rejection.reason) {
    case 'not-png':
      return 'Only PNG files are supported.';
    case 'too-many':
      return 'Please choose a single file.';
    case 'unreadable':
      return 'That file could not be read as an image.';
    case 'wrong-size':
      return `The icon must be exactly ${REQUIRED_ICON_SIZE}×${REQUIRED_ICON_SIZE} px — this one is ${rejection.width}×${rejection.height}.`;
  }
};

/** The eight bytes every PNG starts with. */
const PNG_SIGNATURE = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a];

/**
 * Whether the bytes really are a PNG.
 *
 * `File.type` is metadata, not evidence — browsers derive it from the extension, so a JPEG renamed to
 * `.png` is reported as `image/png`, decodes fine, and would be uploaded under a `Content-Type` that
 * does not match its bytes. The signature is the only thing that settles it client-side.
 *
 * Three-valued rather than boolean: reading the bytes can fail on its own, and "could not be read" is
 * a different thing to tell the user than "not a PNG".
 */
const hasPngSignature = async (file: File): Promise<boolean | 'unreadable'> => {
  let header: Uint8Array;
  try {
    header = new Uint8Array(await file.slice(0, PNG_SIGNATURE.length).arrayBuffer());
  } catch (error) {
    // The first read to touch the disk, and the picker is no guarantee the bytes are still there — a
    // file moved, renamed or unmounted since rejects with `NotReadableError`. Distinguished from a
    // plain `false` so the user is told the file could not be read rather than that it is not a PNG.
    console.warn('Could not read the chosen icon file.', error);
    return 'unreadable';
  }

  return PNG_SIGNATURE.every((byte, index) => header[index] === byte);
};

const decodeAndMeasure = (file: File): Promise<IconCheck> =>
  new Promise((resolve) => {
    const previewUrl = URL.createObjectURL(file);
    const image = new Image();

    image.onload = () => {
      if (image.width !== REQUIRED_ICON_SIZE || image.height !== REQUIRED_ICON_SIZE) {
        URL.revokeObjectURL(previewUrl);
        resolve({ ok: false, reason: 'wrong-size', width: image.width, height: image.height });
        return;
      }
      resolve({ ok: true, previewUrl });
    };

    image.onerror = () => {
      URL.revokeObjectURL(previewUrl);
      resolve({ ok: false, reason: 'unreadable' });
    };

    image.src = previewUrl;
  });

/**
 * Checks a chosen file before any upload is attempted.
 *
 * Both checks have to happen client-side: apps-api hands back a presigned S3 URL and S3 will accept
 * whatever bytes it is given, so nothing downstream would reject a 512×512 icon or a mislabelled JPEG —
 * they would just render wrong everywhere, in every project, until someone noticed.
 *
 * Resolves with an object URL for the preview on success. The caller owns revoking it.
 */
export const checkIconFile = async (file: File): Promise<IconCheck> => {
  if (file.type !== 'image/png') return { ok: false, reason: 'not-png' };

  const signature = await hasPngSignature(file);
  if (signature === 'unreadable') return { ok: false, reason: 'unreadable' };
  if (!signature) return { ok: false, reason: 'not-png' };

  return decodeAndMeasure(file);
};
